import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuthStore } from '../store/authStore';
import queryClient from './queryClient';

// Use same-origin in dev so Vite proxy handles /api and /uploads — works for localhost AND cross-device (e.g. phone at http://192.168.50.122:5173).
// In production, use relative /api. Override with VITE_API_URL if you need a different backend (e.g. deployed API).
const rawApiUrl = import.meta.env.VITE_API_URL;
const baseURL = rawApiUrl
  ? (String(rawApiUrl).endsWith('/api') ? rawApiUrl : `${rawApiUrl.replace(/\/$/, '')}/api`)
  : (import.meta.env.DEV && typeof window !== 'undefined'
    ? `${window.location.origin}/api`
    : '/api');

const CSRF_COOKIE_NAME = import.meta.env.VITE_CSRF_COOKIE_NAME || 'fieldcert_csrf';

function getCookie(name) {
  if (typeof document === 'undefined') return null;
  const cookies = document.cookie ? document.cookie.split('; ') : [];
  for (const c of cookies) {
    if (!c) continue;
    const idx = c.indexOf('=');
    const k = idx >= 0 ? decodeURIComponent(c.slice(0, idx)) : decodeURIComponent(c);
    if (k !== name) continue;
    const v = idx >= 0 ? c.slice(idx + 1) : '';
    return decodeURIComponent(v);
  }
  return null;
}

const api = axios.create({
  baseURL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json', // check-rules:ignore — default for JSON requests; FormData callers override this to undefined
  },
});

let refreshPromise = null;

async function refreshSession() {
  if (!refreshPromise) {
    refreshPromise = api.post('/auth/refresh')
      .catch((err) => {
        throw err;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

// Request interceptor: attach CSRF header; let FormData set Content-Type (do not force application/json)
api.interceptors.request.use(
  (config) => {
    const csrf = getCookie(CSRF_COOKIE_NAME);
    if (csrf) {
      config.headers['x-csrf-token'] = csrf;
    }
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor: attempt refresh once on 401, then retry original request.
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status;
    const original = error.config || {};
    const url = original.url || '';
    // Auth endpoints that must NOT trigger refresh retry (to avoid loops).
    const isNonRefreshableAuthEndpoint =
      url.includes('/auth/login')
      || url.includes('/auth/register')
      || url.includes('/auth/refresh')
      || url.includes('/auth/logout')
      || url.includes('/auth/me')
      || url.includes('/auth/csrf')
      || url.includes('/auth/forgot-password')
      || url.includes('/auth/reset-password');

    if (status === 401 && !original._retry && !isNonRefreshableAuthEndpoint) {
      original._retry = true;
      try {
        await refreshSession();
        return api.request(original);
      } catch (refreshErr) {
        useAuthStore.getState().logoutLocal();
        queryClient.clear();
        toast.error('Session expired. Please sign in again.');
        window.location.href = '/login';
        return Promise.reject(refreshErr);
      }
    }

    if (status === 401 || status === 403) {
      // For auth endpoints or already retried: hard sign-out.
      useAuthStore.getState().logoutLocal();
      queryClient.clear();
      if (!isNonRefreshableAuthEndpoint) {
        toast.error('Session expired. Please sign in again.');
        window.location.href = '/login';
      }
    } else if (status >= 500) {
      const msg = error.response?.data?.error || error.response?.data?.message;
      toast.error(msg || 'Server error. Please try again later.');
    }
    return Promise.reject(error);
  }
);

/**
 * Evidence photo URL for <img src>. Bypasses frontend proxy; requests go directly to backend.
 * Uses VITE_API_URL (stripping /api if present) for environment-aware base.
 * Only allows http/https absolute URLs or relative paths; rejects javascript:, data:, etc.
 * @param {string|null|undefined} url - Full URL, relative path, or empty
 * @returns {string} Absolute URL for the backend (e.g. http://localhost:3000/uploads/evidence/photo.png)
 */
export const getEvidenceDisplayUrl = (url) => {
  if (!url || typeof url !== 'string') return '';

  let path = url.trim().replace(/\\/g, '/');
  if (!path) return '';

  const lower = path.toLowerCase();
  if (lower.startsWith('https://') || lower.startsWith('http://')) return path;
  if (lower.startsWith('javascript:') || lower.startsWith('data:')) return '';

  if (!path.startsWith('/')) path = `/${path}`;

  const baseUrl = import.meta.env.VITE_API_URL
    || (import.meta.env.DEV && typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000');
  return baseUrl + path;
};

/**
 * Upload a photo for evidence. POSTs to /api/uploads and returns the public URL.
 * @param {File} file - Browser File object (e.g. from input type="file")
 * @returns {Promise<string>} Full URL of the uploaded image
 */
export async function uploadPhoto(file) {
  const formData = new FormData();
  formData.append('photo', file);

  const { data } = await api.post('/uploads', formData, {
    headers: {
      'Content-Type': undefined, // check-rules:ignore — explicitly clearing to let browser set multipart boundary
    },
  });
  return data.url;
}

/**
 * Upload organization logo to Supabase. POSTs to /api/upload/logo.
 * @param {File} file - Image file (max 2MB)
 * @returns {Promise<string>} Public URL of the uploaded logo
 */
export async function uploadLogo(file) {
  const formData = new FormData();
  formData.append('logo', file);
  const { data } = await api.post('/upload/logo', formData);
  return data.url;
}

/**
 * Upload the current user's avatar. POSTs to /api/upload/avatar.
 * @param {File} file - Image file (max 2MB)
 * @returns {Promise<string>} Public URL of the uploaded avatar
 */
export async function uploadAvatar(file) {
  const formData = new FormData();
  formData.append('avatar', file);
  const { data } = await api.post('/upload/avatar', formData);
  return data.url;
}

export async function getOutstandingRepairs(siteId, params = {}) {
  const { page = 1, limit = 50 } = params;
  const { data } = await api.get(`/sites/${siteId}/outstanding-repairs`, {
    params: { page, limit },
  });
  return data;
}

/**
 * Get open defects (all unresolved fail/needs_repair test rows, excluding already-repaired items).
 * @param {object} params - { siteId?, elementType?, page?, limit? }
 */
export async function getOpenDefects(params = {}) {
  const { siteId, elementType, page = 1, limit = 50 } = params;
  const { data } = await api.get('/repairs/open', {
    params: {
      ...(siteId ? { siteId } : {}),
      ...(elementType ? { elementType } : {}),
      page,
      limit,
    },
  });
  return data;
}

/**
 * Create a repair record closing an open defect.
 * @param {object} payload - { assetId, sourceTestResultId, beforePhotoUrl, afterPhotoUrl, repairType?, workOrderNumber?, notes? }
 */
export async function createRepairRecord(payload) {
  const { data } = await api.post('/repairs', payload);
  return data;
}

/**
 * Update an existing repair record.
 * @param {number} repairId
 * @param {object} payload - { beforePhotoUrl?, afterPhotoUrl?, proofPhotoUrl?, repairType?, workOrderNumber?, notes? }
 */
export async function updateRepairRecord(repairId, payload) {
  const { data } = await api.put(`/repairs/${repairId}`, payload);
  return data;
}

/**
 * Get repair history for the org.
 * @param {object} params - { siteId?, assetId?, page?, limit? }
 */
export async function getRepairHistory(params = {}) {
  const { siteId, assetId, page = 1, limit = 50 } = params;
  const { data } = await api.get('/repairs', {
    params: {
      ...(siteId ? { siteId } : {}),
      ...(assetId ? { assetId } : {}),
      page,
      limit,
    },
  });
  return data;
}

/**
 * Paginated finalised repair report buckets for the org.
 * @param {object} params - { page?, limit?, siteId? }
 * @returns {Promise<{buckets: object[], pagination: object}>}
 */
export async function getRepairBuckets({ page = 1, limit = 100, siteId } = {}) {
  const { data } = await api.get('/repairs/buckets', {
    params: {
      ...(siteId ? { siteId } : {}),
      page,
      limit,
    },
  });
  return data;
}

/**
 * Open (not-yet-finalised) repair buckets: one row per (site, element type) with
 * complete, unassigned repairs. No `id` — an open bucket only becomes a row once finalised.
 * @param {object} params - { siteId? }
 * @returns {Promise<{buckets: object[]}>}
 */
export async function getOpenRepairBuckets({ siteId } = {}) {
  const { data } = await api.get('/repairs/buckets/open', {
    params: {
      ...(siteId ? { siteId } : {}),
    },
  });
  return data;
}

/**
 * Finalise the open repair bucket for one (site, element type) into a signed-off report.
 * The Generate report popup passes a workOrderNumber when the user types one. A blank
 * (or whitespace-only) value is omitted so the backend can fall back to the repairs'
 * own work order (`mostCommonWorkOrder` in `services/repairBuckets.js`).
 * @param {object} params - { siteId, elementType, workOrderNumber? }
 * @returns {Promise<object>} the created repair_report_buckets row (snake_case, includes `id`)
 */
export async function finaliseRepairBucket({ siteId, elementType, workOrderNumber } = {}) {
  const body = { siteId, elementType };
  const trimmed = workOrderNumber != null ? String(workOrderNumber).trim() : '';
  if (trimmed) {
    body.workOrderNumber = trimmed;
  }
  const { data } = await api.post('/repairs/buckets/finalise', body);
  return data;
}

/**
 * Fetch all paginated sites for selector-style consumers.
 * Keeps page size within backend limits and walks pages until exhausted.
 */
export async function getAllSites(params = {}) {
  const limit = Math.min(Math.max(parseInt(params.limit, 10) || 100, 1), 100);
  const baseParams = { ...params };
  delete baseParams.page;
  delete baseParams.limit;

  let page = 1;
  let hasNextPage = true;
  const allSites = [];
  const safetyPageCap = 500;

  while (hasNextPage && page <= safetyPageCap) {
    const { data } = await api.get('/sites', { params: { ...baseParams, page, limit } });
    const rows = data?.sites ?? [];
    allSites.push(...rows);

    const pagination = data?.pagination;
    if (!pagination) break;
    hasNextPage = Boolean(pagination.hasNextPage);
    page += 1;
  }

  return allSites;
}

function arrayBufferToBase64(buffer) {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    binary += String.fromCharCode(...chunk);
  }
  return btoa(binary);
}

export async function sendReportEmail({
  reportType,
  sessionId,
  siteId,
  workOrderNumber,
  recipientEmail,
  pdfBlob,
  fileName,
  messageHtml,
}) {
  const arrayBuffer = await pdfBlob.arrayBuffer();
  const pdfBase64 = arrayBufferToBase64(arrayBuffer);
  const { data } = await api.post('/reports/email', {
    reportType,
    sessionId: sessionId != null && String(sessionId).trim() !== '' ? sessionId : undefined,
    siteId: siteId != null ? siteId : undefined,
    workOrderNumber: workOrderNumber != null ? workOrderNumber : undefined,
    recipientEmail,
    pdfBase64,
    fileName,
    messageHtml,
  });
  return data;
}

export default api;
